import React, {useCallback, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import StyledEditorLinkPopup from 'components/molecules/Editors/EditorLinkPopup/EditorLinkPopup.styles';
import {useRichTextEditorContext} from 'mui-tiptap';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import constants from 'helpers/constants';
import {getMarkRange, getMarkType, isNodeSelection, posToDOMRect} from '@tiptap/core';
import Box from '@mui/material/Box';
import Button from 'components/atoms/Buttons/Button/Button';
import Icon from 'components/atoms/Icons/Icon/Icon';
import Close from '@mui/icons-material/Close';
import Save from '@mui/icons-material/Save';
import Delete from '@mui/icons-material/Delete';

const LinkMenuState = {
  HIDDEN: 0,
  VIEW_LINK_DETAILS: 1,
  EDIT_LINK: 2
};

const EditorLinkPopup = React.forwardRef((props, ref) => {
  const innerProps = useComponentProps(props, 'EditorLinkPopup');

  const innerRef = useRef(null);
  const formRef = useRef(null);

  useImperativeHandle(ref, () => innerRef.current);

  const [busy, setBusy] = useState(false);

  const editor = useRichTextEditorContext();

  const handlerStorage = editor?.storage?.linkBubbleMenuHandler;
  const menuState = handlerStorage?.state;
  const open = menuState !== LinkMenuState.HIDDEN;

  const linkRange = getMarkRange(
    editor?.state?.selection?.$to,
    getMarkType('link', editor?.schema)
  );
  const selectedText = editor?.state?.doc?.textBetween(
    editor?.state?.selection?.$from?.pos,
    editor?.state?.selection?.$to?.pos
  );
  const linkText = linkRange ? editor.state.doc.textBetween(linkRange.from, linkRange.to) : (
    selectedText ? selectedText : ''
  );
  const currentHref = editor?.getAttributes('link').href ?? '';

  useLayoutEffect(() => {
    setBusy(false);
  }, [open]);

  const fields = useMemo(() => {
    const fields = [];

    if (open) {
      fields.push({
        name: 'text',
        placeholder: 'Text',
        type: constants.formFieldTypes.text,
        validation: constants.formFieldValidationTypes.text,
        initial: linkText ?? '',
        required: true,
        FormFieldProps: {
          showTooltip: true,
          variant: 'staticLabel',
          size: 'smaller',
          InputLabelProps: {
            shrink: true,
          }
        }
      });

      fields.push({
        name: 'link',
        placeholder: 'Link',
        type: constants.formFieldTypes.text,
        validation: constants.formFieldValidationTypes.link,
        format: constants.formFieldFormatTypes.url,
        initial: currentHref ?? '',
        required: true,
        FormFieldProps: {
          showTooltip: true,
          variant: 'staticLabel',
          size: 'smaller',
          InputLabelProps: {
            shrink: true,
          }
        }
      });
    }

    return fields;
  }, [open, linkText, currentHref]);

  const handleToggle = (open) => {
    if (!open) {
      setBusy(false);
      editor?.commands?.closeLinkBubbleMenu?.();
    }
  };

  const handleSubmit = useCallback((values) => {
    editor.chain()
      .extendMarkRange('link')
      .insertContent({
        type: 'text',
        marks: [
          {
            type: 'link',
            attrs: {
              href: values.link
            },
          },
        ],
        text: values.text
      })
      .setLink({
        href: values.link,
      })
      .focus()
      .run();

    setBusy(false);
    editor?.commands?.closeLinkBubbleMenu?.();
  }, [editor]);

  const handleSubmitClick = (e) => {
    formRef.current?.submit();
    e.preventDefault();
  }

  const handleDelete = (e) => {
    editor.chain()
      .unsetLink()
      .setTextSelection(editor.state.selection.to)
      .focus()
      .run();

    editor?.commands?.closeLinkBubbleMenu?.();
    e.preventDefault();
  }

  const handleCancel = (e) => {
    editor?.commands?.closeLinkBubbleMenu?.();
    e.preventDefault();
  }

  const renderButtons = () => {
    return <React.Fragment>
      <Button children={'Cancel'}
              variant="text"
              size="small"
              startIcon={<Icon icon={Close}/>}
              onClick={handleCancel}/>
      <Button color="error"
              variant="contained"
              children="Remove"
              size="small"
              startIcon={<Icon icon={Delete}/>}
              onClick={handleDelete}/>
      <Button type="submit"
              color="primary"
              variant="contained"
              children="Save"
              size="small"
              startIcon={<Icon icon={Save}/>}
              onClick={handleSubmitClick}/>
    </React.Fragment>
  }

  innerProps.className = utils.flattenClassName(innerProps.className);

  if (editor?.isEditable) {
    const anchorEl = {
      getBoundingClientRect: () => {
        const { ranges } = editor.state.selection;
        const from = Math.min(...ranges.map((range) => range.$from.pos));
        const to = Math.max(...ranges.map((range) => range.$to.pos));

        if (isNodeSelection(editor.state.selection)) {
          const node = editor.view.nodeDOM(from);

          if (node instanceof HTMLElement) {
            return node.getBoundingClientRect();
          }
        }

        return posToDOMRect(editor.view, from, to);
      }
    };

    innerProps.anchorEl = innerProps.anchorEl ?? anchorEl;

    return <StyledEditorLinkPopup ref={innerRef} {...innerProps}
                                  fullWidth={true}
                                  autoClose={!busy}
                                  onToggle={handleToggle}
                                  open={open}>
      <InlineForm ref={formRef}
                  className="EditorLinkPopup-form"
                  fields={fields}
                  propagate={false}
                  onSubmit={handleSubmit}
                  onBusy={setBusy}/>
      <Box className="EditorLinkPopup-buttons">
        {renderButtons()}
      </Box>
    </StyledEditorLinkPopup>
  }
});

EditorLinkPopup.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  items: PropTypes.array,
  command: PropTypes.func,
  open: PropTypes.bool
};

export default EditorLinkPopup;
